import { useCallback, useState } from 'react';
import DomPurify from 'dompurify';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { Button, Checkbox } from '@upmc/ims.component-library';
import styles from '../../../ApplicationMaintenance.module.scss';
import MaterialIcon from '@material/react-material-icon';
import Section from '../../../components/NewApplication/Section';
import ADGroupSearch from './ADGroupSearch';
import ExtensionTypeSelect from '../../../components/ExtensionTypeSelect';
import Base from '../../../../Base/Base';
import AppADGroupRequest from '../../../../../Classes/ResponseModels/ApplicationManagement/AppADGroupRequest';
import AppCloudGroupRequest from 'Classes/ResponseModels/ApplicationManagement/AppCloudGroupRequest';
import {
	AppADGroup,
	AppCloudGroup,
	AppEUGroup,
} from '../../../../../Classes/ResponseModels/Apps';
import {
	CreateAppADGroup,
	CreateAppCloudGroup,
	CreateAppEUGroup,
} from '../../../_api';
import { AD_TYPES } from 'pages/ApplicationMaintenance/_constants';
import ApiErrors from '../../../components/NewApplication/ApiErrors';
import AppEUGroupRequest from 'Classes/ResponseModels/ApplicationManagement/AppEUGroupRequest';

const ADGroupMapping = ({
	adXREF = [],
	appID,
	app,
	readOnly,
	type = AD_TYPES.ON_PREM,
	currentUser,
	requirementNumber,
	reload,
	apiErrors,
	setApiErrors,
}) => {
	const mappingLogicOptions = [
		{
			label: 'All Users',
			statement:
				type === AD_TYPES.EU
					? `Generic_All_EU_Users_Logic ${appID}, `
					: `Generic_All_Users_Logic ${appID}, `,
			id: 1,
		},
		{
			label: 'If Extension Value Equals',
			statement:
				type === AD_TYPES.EU
					? 'Generic_If_EU_Extension_Value_Equals_Logic'
					: 'Generic_If_Extension_Value_Equals_Logic',
			id: 2,
		},
		{
			label: 'Custom',
			id: 3,
		},
	];
	const labelType =
		type === AD_TYPES.CLOUD
			? 'Cloud Group'
			: type === AD_TYPES.EU
			? 'EU Group'
			: 'AD Group';

	const [modalOpen, setModalOpen] = useState(false);
	/**
	 * @type {[import('Classes/ResponseModels/Apps').ADGroup|import('Classes/ResponseModels/Apps').CloudGroup, Function]}
	 */
	const [selectedADGroup, setSelectedADGroup] = useState(null);
	const [mappingLogic, setMappingLogic] = useState(mappingLogicOptions[0]);
	const [sCustomOptions, setCustomOptions] = useState([]);
	const [sCustomOption, setCustomOption] = useState(null);
	const [sCustomArgument, setCustomArgument] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [extensionTypeID, setExtensionTypeID] = useState(0);
	const [extensionTypeValue, setExtensionTypeValue] = useState('');
	const [sIsEmailRequired, setIsEmailRequired] = useState(false);

	const handleADGroupSelect = useCallback(group => {
		setSelectedADGroup(group);
	}, []);

	const handleMappingLogicChange = e => {
		let logicOption = mappingLogicOptions.find(
			opt => opt.id === e.target.value
		);
		if (logicOption) {
			setMappingLogic(logicOption);

			if (logicOption.id === 3 && sCustomOptions.length < 1) {
				const arrProcs = adXREF
					.map(xref => xref.SQLStatement?.split(' ')?.[0])
					.reduce((prev, current) => {
						if (
							!(
								current?.trim().toLowerCase() ===
									mappingLogicOptions[0].statement
										.split(' ')[0]
										.trim()
										.toLowerCase() ||
								current?.trim().toLowerCase() ===
									mappingLogicOptions[1].statement
										.split(' ')[0]
										.trim()
										.toLowerCase() ||
								prev.findIndex(
									el =>
										el.toLowerCase() ===
										current?.trim().toLowerCase()
								) > -1
							)
						) {
							prev.push(current?.trim());
						}
						return prev;
					}, []);
				setCustomOptions(arrProcs);
				setCustomOption(arrProcs[0]);
			}
		}
	};

	const handleSave = () => {
		let request;
		let sqlStatement;
		let xref;

		switch (mappingLogic?.id) {
			case 2:
				sqlStatement = `${mappingLogic.statement} ${extensionTypeID}, '${extensionTypeValue}', `;
				break;
			case 3:
				sqlStatement = `${sCustomOption.trim()} ${sCustomArgument.trim()}, `;
				break;
			default:
				sqlStatement = mappingLogic.statement;
		}

		switch (type) {
			case AD_TYPES.CLOUD:
				request = new AppCloudGroupRequest(app, currentUser);
				request.RequirementNumber = requirementNumber;

				xref = new AppCloudGroup();
				xref.id = -1;
				xref.appID = appID;
				xref.groupDisplayName = selectedADGroup.displayName;
				xref.active = true;
				xref.sqlStatement = sqlStatement;
				xref.emailRequired = sIsEmailRequired;
				request.AppCloudGroupXREF = xref;

				CreateAppCloudGroup(request)
					.then(response => {
						setApiErrors(response.App.Errors);
						if (response.App.Errors.length < 1) {
							reload();
						}
					})
					.then(() => {
						setIsSaving(false);
					});
				break;
			case AD_TYPES.EU:
				request = new AppEUGroupRequest(app, currentUser);
				request.RequirementNumber = requirementNumber;

				xref = new AppEUGroup();
				xref.id = -1;
				xref.appID = appID;
				xref.groupName = selectedADGroup.samAccountName;
				xref.active = true;
				xref.sqlStatement = sqlStatement;
				request.AppEUGroupXREF = xref;

				CreateAppEUGroup(request)
					.then(response => {
						setApiErrors(response.App.Errors);
						if (response.App.Errors.length < 1) {
							reload();
						}
					})
					.then(() => {
						setIsSaving(false);
					});
				break;
			case AD_TYPES.ON_PREM:
				request = new AppADGroupRequest(app, currentUser);
				request.RequirementNumber = requirementNumber;

				request.AppADGroupXref = new AppADGroup();
				request.AppADGroupXref.adGroup = selectedADGroup;
				request.AppADGroupXref.sqlStatement = sqlStatement;
				request.AppADGroupXref.aDGroup_SID = selectedADGroup.objectSID;
				request.AppADGroupXref.appID = appID;
				request.AppADGroupXref.id = -1;

				CreateAppADGroup(request)
					.then(response => {
						setApiErrors(response.App.Errors);
						if (response.App.Errors.length < 1) {
							reload();
						}
					})
					.then(() => {
						setIsSaving(false);
					});
				break;
			default:
				break;
		}
	};

	const MappingLogicSelect = () => {
		return (
			<>
				<p>Select Mapping Logic</p>
				<Select
					labelId='Mapping_Logic_Select'
					id='Mapping_Logic_Select'
					value={mappingLogic?.id}
					label='MappingLogicType'
					onChange={handleMappingLogicChange}
					name='mappingLogic'
				>
					{mappingLogicOptions.map(option => {
						return (
							<MenuItem key={option.id} value={option.id}>
								{option.label}
							</MenuItem>
						);
					})}
				</Select>
				<br />
				{type === AD_TYPES.CLOUD && (
					<Box sx={{ marginTop: '8px' }}>
						<Checkbox
							size='small'
							checked={sIsEmailRequired}
							onChange={() =>
								setIsEmailRequired(
									isEmailRequired => !isEmailRequired
								)
							}
							value={sIsEmailRequired}
							label='Group Membership Requires Email'
						/>
					</Box>
				)}
			</>
		);
	};

	const ConditionalLogicComponent = () => {
		switch (mappingLogic?.id) {
			case 1:
				//All Users, requires no additional logic, appID parameter assigned in mappingLogicOptions definition
				break;
			case 2:
				return (
					<>
						<hr></hr>
						<h4>Set 'If Extension Value Equals' Parameters</h4>

						<ExtensionTypeSelect
							extensionTypes={app.extensions ?? []}
							handleChange={e =>
								setExtensionTypeID(e.target.value)
							}
							extensionID={extensionTypeID ?? null}
						/>
						<br></br>
						<br></br>
						<p>
							Check Value (if request extension value equals this
							value, selected group will be mapped)
						</p>

						<input
							type='text'
							onChange={e =>
								setExtensionTypeValue(e.target.value)
							}
						/>
					</>
				);
			case 3:
				return (
					<>
						<hr />
						<p>Select Existing Logic</p>
						<FormControl>
							<Select
								id='mapping__select--custom'
								disabled={sCustomOptions.length < 1}
								value={sCustomOption}
								onChange={e => setCustomOption(e.target?.value)}
								name='customMappingSelect'
							>
								{sCustomOptions.map(option => {
									return (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									);
								})}
							</Select>
							<FormHelperText>
								{sCustomOptions.length > 0
									? ' '
									: 'No custom procedures available.'}
							</FormHelperText>
						</FormControl>
						{sCustomOption && (
							<>
								<br />
								<TextField
									id='mapping__textbox--custom'
									label='Argument(s) to Append'
									placeholder={`numericArg, 'stringArg'`}
									variant='outlined'
									helperText='Trailing comma to be omitted.'
									onChange={e =>
										setCustomArgument(
											DomPurify.sanitize(e.target.value)
										)
									}
								/>
							</>
						)}
					</>
				);
			default:
				break;
		}
	};

	return (
		<div>
			<div className={styles.controlsContainer}>
				<Button
					variant='outlined'
					id='AdGroup_Mapping_Modal'
					onClick={() => setModalOpen(true)}
					startIcon={<MaterialIcon icon='add' />}
					disabled={readOnly}
				>
					Create
					{' ' + labelType + ' '}
					Mapping
				</Button>
			</div>
			<Modal
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
				}}
			>
				<div className={styles.Modal}>
					<Base title={`Create ${labelType} Mapping`}>
						<div className={styles.controlsContainer}>
							<Button
								onClick={() => {
									setIsSaving(true);
									handleSave();
								}}
								startIcon={<MaterialIcon icon='save' />}
								className={styles.editButton}
								id='ADGroupMapping_Save'
								disabled={readOnly || isSaving}
							>
								Save
							</Button>
						</div>
						<ApiErrors errors={apiErrors} />
						<Section label={`${labelType} Mapping`}>
							{selectedADGroup === null ? (
								<ADGroupSearch
									type={type}
									handleSelect={handleADGroupSelect}
								/>
							) : (
								<div className={styles.row}>
									<strong>
										Mapping:{' '}
										{type === AD_TYPES.CLOUD
											? selectedADGroup.displayName
											: selectedADGroup.samAccountName}
									</strong>

									<Button
										onClick={() => setSelectedADGroup(null)}
										variant='outlined'
										sx={{ mx: '.5em' }}
									>
										Reset Selection
									</Button>
								</div>
							)}
							<br></br>
							{selectedADGroup ? <MappingLogicSelect /> : null}
							{selectedADGroup
								? ConditionalLogicComponent()
								: null}
						</Section>
						<div className={styles.controlsContainer}>
							<Button
								onClick={() => {
									setIsSaving(true);
									handleSave();
								}}
								startIcon={<MaterialIcon icon='save' />}
								className={styles.editButton}
								id='ADGroupMapping_Save2'
								disabled={
									readOnly ||
									isSaving ||
									(mappingLogic.id === 3 &&
										sCustomOptions.length < 1)
								}
							>
								Save
							</Button>
						</div>
					</Base>
				</div>
			</Modal>
		</div>
	);
};

export default ADGroupMapping;
